<script setup lang="ts">
import { computed, h, inject, SetupContext, VNode } from 'vue';

import { ToggleSwitchEmits, ToggleSwitchProps } from './ToggleSwitch.vue.d';
import Icon from '../icon/Icon.vue';

const props = withDefaults(defineProps<ToggleSwitchProps>(), {
  trueValue: true,
  falseValue: false,
  disabled: false,
  readonly: false,
  tooltipPos: 'top',
  triState: false,
});

const emit = defineEmits<ToggleSwitchEmits>();

const Preset = inject<Record<string, any>>('preset', {}).toggleswitch;
const isChecked = computed(() => props.modelValue === props.trueValue);

const handleChange = (event: Event): void => {
  if (!props.disabled && !props.readonly) {
    let newValue: boolean | null;

    if (!props.triState) {
      newValue = isChecked.value ? props.falseValue : props.trueValue;
    } else if (props.triState) {
      newValue =
        // eslint-disable-next-line no-nested-ternary
        props.modelValue == null
          ? props.trueValue
          : isChecked.value
            ? props.falseValue
            : null;
    }

    emit('update:modelValue', newValue);
    emit('change', event);
  }
};

const handleFocus = (event: FocusEvent): void => {
  emit('focus', event);
};

const handleBlur = (event: FocusEvent): void => {
  emit('blur', event);
};

const Label = (propsData: object, setupContext?: SetupContext): VNode => {
  const { attrs, slots } = setupContext as any;
  return props.label ? h('label', attrs, slots.default()) : slots.default();
};
</script>

<template>
  <Label v-bind="Preset.label">
    <span
      v-bind="Preset.root({ props })"
      :data-p-disabled="props.disabled"
      :data-p-highlight="isChecked"
      @click.stop
      data-wv-name="toggleswitch"
    >
      <input
        v-bind="Preset.input({ props })"
        :id="props.inputId"
        :aria-checked="isChecked"
        :aria-invalid="props.invalid || undefined"
        :aria-label="props.ariaLabel"
        :aria-labelledby="props.ariaLabelledby"
        :checked="isChecked"
        :class="['input-switch-input', props.inputClass]"
        :disabled="props.disabled"
        :readonly="props.readonly"
        :style="props.inputStyle"
        :tabindex="props.tabindex"
        @blur="handleBlur"
        @change="handleChange"
        @focus="handleFocus"
        role="switch"
        type="checkbox"
      />

      <span v-bind="Preset.slider({ props })" />
    </span>

    <span
      v-if="props.label"
      data-wv-section="labeltext"
      v-bind="Preset.labeltext"
    >
      {{ label }}
    </span>

    <Icon
      v-if="tooltip"
      :info="tooltip"
      :tooltip-pos="tooltipPos"
      v-bind="Preset.tooltipicon"
      data-wv-section="tooltipicon"
      icon="info"
    />
  </Label>
</template>
